import React, { useState } from 'react';
import { products } from '../data/products';
import categories from '../data/categories_update';
import { 
  Box, 
  Typography, 
  Divider, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TableSortLabel, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Chip,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Grid,
  CardMedia
} from '@mui/material';
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';

const columns = [
  { id: 'sr', label: 'Sr No.' },
  { id: 'name', label: 'Product Name' },
  { id: 'brand', label: 'Brand' },
  { id: 'composition', label: 'Composition' },
  { id: 'category', label: 'Category' },
  { id: 'packSize', label: 'Pack Size' },
  { id: 'packing', label: 'Packing' }
];

function getComparator(order, orderBy) {
  return (a, b) => {
    if (orderBy === 'sr') {
      return order === 'asc' ? a.id - b.id : b.id - a.id;
    }
    if (a[orderBy] < b[orderBy]) return order === 'asc' ? -1 : 1;
    if (a[orderBy] > b[orderBy]) return order === 'asc' ? 1 : -1;
    return 0;
  };
}

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function Products() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('sr');
  const query = useQuery();
  const selectedCategory = query.get('category');
  const navigate = useNavigate();
  const [categorySelect, setCategorySelect] = useState('');

  const handleSort = (columnId) => {
    if (orderBy === columnId) {
      setOrder(order === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(columnId);
      setOrder('asc');
    }
  };

  let filteredProducts = products;
  if (selectedCategory) {
    filteredProducts = products.filter(p => p.category === selectedCategory);
  }

  const sortedProducts = [...filteredProducts].sort(getComparator(order, orderBy));

  const handleClearFilter = () => {
    navigate('/products');
    setCategorySelect('');
  };

  const handleCategoryChange = (event) => {
    const value = event.target.value;
    setCategorySelect(value);
    if (value) {
      navigate(`/products?category=${encodeURIComponent(value)}`);
    }
  };

  const isSugarFree = (product) => {
    return product.category === "Expectorant / Cough Syrup" && 
           product.name.toLowerCase().includes('sugar free');
  };

  const formatComposition = (composition) => {
    if (!composition) return '';
    return composition.split(',').map((item, index) => (
      <React.Fragment key={index}>
        {index > 0 && <br />}
        • {item.trim()}
      </React.Fragment>
    ));
  };

  const MobileProductCard = ({ product, index }) => (
    <Card 
      sx={{ 
        mb: 2,
        backgroundColor: isSugarFree(product) ? 'rgba(76, 175, 80, 0.1)' : 'inherit',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Typography variant="h6" component="div">
            {product.name}
            {isSugarFree(product) && (
              <Chip 
                label="Sugar Free" 
                size="small" 
                color="success"
                sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.75rem' }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            #{index + 1}
          </Typography>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 2,
          height: '150px',
          backgroundColor: '#f5f5f5',
          borderRadius: 1,
          overflow: 'hidden'
        }}>
          {product.image ? (
            <CardMedia
              component="img"
              image={`/images/${product.image}`}
              alt={product.name}
              sx={{
                height: '100%',
                width: 'auto',
                objectFit: 'contain'
              }}
            />
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              color: 'text.secondary'
            }}>
              <BrokenImageIcon sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="body2">Image Not Available</Typography>
            </Box>
          )}
        </Box>
        
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Brand</Typography>
            <Typography variant="body1">{product.brand}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Category</Typography>
            <Typography variant="body1">{product.category}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body2" color="text.secondary">Composition</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line', pl: 2 }}>
              {formatComposition(product.composition)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Pack Size</Typography>
            <Typography variant="body1">{product.packSize}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" color="text.secondary">Packing</Typography>
            <Typography variant="body1">{product.packing}</Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: { xs: '100%', sm: 1200 }, mx: 'auto' }}>
      <Typography variant="h3" gutterBottom>Our Products</Typography>
      <Divider sx={{ mb: 3 }} />
      <Typography variant="body1" sx={{ mb: 4 }}>
        Explore our diverse range of research-based healthcare solutions. From cardiovascular care to pain management, we prioritize human well-being with quality pharmaceuticals for every need.
      </Typography>
      
      {selectedCategory ? (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            Showing products for category: <b>{selectedCategory}</b>
          </Typography>
          <Button variant="outlined" size="small" onClick={handleClearFilter}>Clear Filter</Button>
        </Box>
      ) : (
        <Box sx={{ mb: 2, maxWidth: 300 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="category-filter-label">Filter by Category</InputLabel>
            <Select
              labelId="category-filter-label"
              value={categorySelect}
              label="Filter by Category"
              onChange={handleCategoryChange}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {categories.map(cat => (
                <MenuItem key={cat.name} value={cat.name}>{cat.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {isMobile ? (
        <Box>
          {sortedProducts.map((product, idx) => (
            <MobileProductCard key={product.id} product={product} index={idx} />
          ))}
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                    <TableSortLabel
                      active={orderBy === col.id}
                      direction={orderBy === col.id ? order : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedProducts.map((product, idx) => (
                <TableRow 
                  key={product.id}
                  sx={{
                    backgroundColor: isSugarFree(product) ? 'rgba(76, 175, 80, 0.1)' : 'inherit',
                    '&:hover': {
                      backgroundColor: isSugarFree(product) 
                        ? 'rgba(76, 175, 80, 0.2)' 
                        : 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ 
                        width: '80px', 
                        height: '80px', 
                        backgroundColor: '#f5f5f5',
                        borderRadius: 1,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        {product.image ? (
                          <CardMedia
                            component="img"
                            image={`/images/${product.image}`}
                            alt={product.name}
                            sx={{
                              height: '100%',
                              width: 'auto',
                              objectFit: 'contain'
                            }}
                          />
                        ) : (
                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            color: 'text.secondary'
                          }}>
                            <BrokenImageIcon sx={{ fontSize: 24 }} />
                          </Box>
                        )}
                      </Box>
                      <Box>
                        <Typography variant="body1">
                          {product.name}
                          {isSugarFree(product) && (
                            <Chip 
                              label="Sugar Free" 
                              size="small" 
                              color="success"
                              sx={{ ml: 1, fontWeight: 'bold', fontSize: '0.75rem' }}
                            />
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell sx={{ whiteSpace: 'pre-line', paddingLeft: '24px' }}>
                    {formatComposition(product.composition)}
                  </TableCell>
                  <TableCell>{product.category}</TableCell>
                  <TableCell>{product.packSize}</TableCell>
                  <TableCell>{product.packing}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}